// Captures the candidate's microphone as a WAV, in parallel with the video.
//
// WHY THIS EXISTS AT ALL
// The interview already records webm via MediaRecorder, and that file is what a
// human reviews. It cannot also be the transcription input: the API accepts wav
// and mp3 and rejects webm outright — not an undocumented gap, an explicit
// "Valid formats are: [wav, mp3]". Converting server-side needs ffmpeg, which
// means a native binary in an Azure App Service deploy for a job the browser can
// do for free, on audio it already holds as PCM.
//
// So each answer produces two artefacts from one microphone: the webm video for
// people, and this WAV for the model. The WAV is never stored.
//
// WHY 16 kHz MONO
// The provider downsamples to 16 kHz mono anyway, and bills per second of audio
// rather than per byte. Sending 48 kHz stereo would triple the upload on the
// candidate's connection — the thing most likely to fail mid-interview — and buy
// nothing. Two minutes at 16 kHz mono is about 3.8 MB.

const TARGET_SAMPLE_RATE = 16000;

// Runs on the audio thread and forwards raw frames. Inlined as a Blob URL rather
// than shipped as a separate file because CRA would need public/ plumbing and a
// cache-busting story for one twelve-line processor, and a worklet that 404s in
// production fails at record time on the candidate's machine.
const WORKLET_SOURCE = `
class PcmCollector extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    // Copy: the runtime reuses this buffer for the next render quantum.
    if (channel && channel.length) this.port.postMessage(new Float32Array(channel));
    return true;
  }
}
registerProcessor("pcm-collector", PcmCollector);
`;

// Averaging decimator, not a plain "take every Nth sample".
//
// Dropping samples aliases anything above the new Nyquist frequency back down
// into the speech band as a metallic warble. It survives casual listening and
// then shows up as a worse transcript, which is exactly the kind of fault that
// gets blamed on the model.
function downsample(input, fromRate, toRate) {
  if (toRate >= fromRate) return input;
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += input[j];
    out[i] = end > start ? sum / (end - start) : 0;
  }
  return out;
}

function encodeWav(samples, sampleRate) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const ascii = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };

  ascii(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true); // PCM header size
  view.setUint16(20, 1, true); // format: PCM
  view.setUint16(22, 1, true); // channels: mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, "data");
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1) {
    // Clamp before scaling. A sample above 1.0 wraps to a large negative
    // integer, which is heard as a click and read as a glottal stop.
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([view], { type: "audio/wav" });
}

/**
 * Starts capturing `stream`'s microphone track.
 *
 * Returns a handle with `stop()` — resolving to `{ blob, durationSeconds }`, or
 * to null if nothing usable was captured — and `cancel()` for teardown.
 *
 * NEVER THROWS ON A CAPTURE FAILURE. The video recording is the artefact that
 * matters and it is already running by the time this is called; taking the
 * interview down because a WAV could not be produced would trade a missing
 * transcript, which a human can replace by watching the recording, for a lost
 * answer, which nobody can.
 */
export async function startWavCapture(stream) {
  const audioTracks = stream?.getAudioTracks?.() || [];
  if (!audioTracks.length) return null;

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;

  let ctx;
  let source;
  let node;
  let workletUrl;
  const chunks = [];
  let stopped = false;

  const teardown = () => {
    stopped = true;
    try {
      if (node) node.disconnect();
      if (source) source.disconnect();
    } catch {
      /* already torn down */
    }
    if (workletUrl) URL.revokeObjectURL(workletUrl);
    if (ctx && ctx.state !== "closed") ctx.close().catch(() => {});
  };

  try {
    ctx = new AudioCtx();
    // Autoplay policy suspends a context created outside a gesture. Recording
    // starts from a button press, but resuming is cheap and a suspended context
    // produces a silent file rather than an error.
    if (ctx.state === "suspended") await ctx.resume();

    source = ctx.createMediaStreamSource(new MediaStream(audioTracks));

    if (ctx.audioWorklet) {
      workletUrl = URL.createObjectURL(
        new Blob([WORKLET_SOURCE], { type: "application/javascript" }),
      );
      await ctx.audioWorklet.addModule(workletUrl);
      node = new AudioWorkletNode(ctx, "pcm-collector");
      node.port.onmessage = (e) => {
        if (!stopped) chunks.push(e.data);
      };
      // A worklet with no downstream connection is not pulled in some engines.
      // Routing through a silent gain keeps it running without the candidate
      // hearing themselves, which is disorienting and induces feedback howl.
      const silence = ctx.createGain();
      silence.gain.value = 0;
      source.connect(node);
      node.connect(silence);
      silence.connect(ctx.destination);
    } else {
      // Safari and older engines. Deprecated and main-thread, but for a
      // two-minute answer the alternative is no transcript at all.
      node = ctx.createScriptProcessor(4096, 1, 1);
      node.onaudioprocess = (e) => {
        if (!stopped) chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      source.connect(node);
      node.connect(ctx.destination);
    }
  } catch (err) {
    console.warn("[wavRecorder] capture unavailable:", err?.message || err);
    teardown();
    return null;
  }

  const sourceRate = ctx.sampleRate;

  return {
    cancel: teardown,
    async stop() {
      if (stopped) return null;
      const captured = chunks.slice();
      teardown();

      const total = captured.reduce((n, c) => n + c.length, 0);
      if (!total) return null;

      const merged = new Float32Array(total);
      let offset = 0;
      for (const chunk of captured) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }

      const resampled = downsample(merged, sourceRate, TARGET_SAMPLE_RATE);
      return {
        blob: encodeWav(resampled, TARGET_SAMPLE_RATE),
        durationSeconds: resampled.length / TARGET_SAMPLE_RATE,
        // ── Did they actually say anything? ─────────────────────────────
        //
        // Returned here because the samples are already in hand — measuring
        // them costs one pass over an array that was just built, and the
        // alternative is decoding the encoded WAV again in the component.
        //
        // The server measures the same thing on the same audio and is the
        // authority; this copy exists so the CANDIDATE can be told, while they
        // are still sitting there and can do something about it, that their
        // recording contains no voice. Finding that out in review helps nobody.
        //
        // Thresholds deliberately match utils/hiringAudioLevel on the server.
        // Two detectors that disagree is worse than one: a candidate waved
        // through here and suppressed there would be told their answer was
        // fine and then scored as absent.
        ...measureSpeech(resampled, TARGET_SAMPLE_RATE),
      };
    },
  };
}

// ── Speech detection, mirrored from utils/hiringAudioLevel.js ───────────────
//
// These constants and this arithmetic are deliberately identical to the
// server's. The server remains the authority — it decides what gets
// transcribed — and this is only here so the candidate can be told in time to
// re-record. If the two ever disagree, someone is told their answer was fine
// and then scored as having said nothing, which is the worst of both.
//
// The bias is the same one-way bias: every threshold is set so that anything
// remotely like a voice counts as speech. Wrongly suppressing a real answer is
// far worse than wrongly accepting a silent one.
const FRAME_MS = 20;
const FRAME_FLOOR_DBFS = -50;
const PEAK_FLOOR_DBFS = -45;
const MIN_VOICED_MS = 300;

const dbfs = (amp) => (amp <= 0 ? -Infinity : 20 * Math.log10(amp));

function measureSpeech(samples, sampleRate) {
  const windowSize = Math.max(1, Math.round((FRAME_MS / 1000) * sampleRate));
  let peak = 0;
  let voicedWindows = 0;
  let firstVoiced = -1;
  let windowIndex = 0;

  for (let i = 0; i < samples.length; i += windowSize) {
    let sum = 0;
    let n = 0;
    for (let j = i; j < i + windowSize && j < samples.length; j += 1) {
      const s = samples[j];
      sum += s * s;
      const mag = Math.abs(s);
      if (mag > peak) peak = mag;
      n += 1;
    }
    if (n && dbfs(Math.sqrt(sum / n)) > FRAME_FLOOR_DBFS) {
      voicedWindows += 1;
      if (firstVoiced === -1) firstVoiced = windowIndex;
    }
    windowIndex += 1;
  }

  const voicedMs = voicedWindows * FRAME_MS;
  return {
    hasSpeech: dbfs(peak) >= PEAK_FLOOR_DBFS && voicedMs >= MIN_VOICED_MS,
    voicedMs,
    speechOnsetMs: firstVoiced >= 0 ? firstVoiced * FRAME_MS : null,
  };
}

export const _internal = {
  downsample,
  encodeWav,
  measureSpeech,
  TARGET_SAMPLE_RATE,
};
